import React, {useState, useContext, useEffect, useRef} from "react";
import CloseIcon from "./CloseIcon";
import {PageHeading} from "../ui-components/Container";
import {FormattedMessage, useIntl} from "react-intl";
import Input from "../ui-components/Input";
import Textarea from "../ui-components/Textarea";
import Button from "../ui-components/Button";
import {TrashIcon, CheckIcon, PlusIcon, PencilIcon} from "@heroicons/react/24/solid";
import Label from "../ui-components/Label";
import Toggle from "../ui-components/Toggle";
import {NotificationsContext} from "../ui-components/Notifications";
import ServiceConfiguratorBlockExpressionConfigurator from "./ServiceConfiguratorBlockExpressionConfigurator";
import {isValidRecursiveQuestion} from "../utils/blockValidation";
import CountryContext from "../CountryContext";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import useDispatchServiceAp from "../hooks/useDispatchServiceAp";
import SearchSelect from "../ui-components/SearchSelect";
import useFetchApItems from "../hooks/useFetchApItems";
import Tooltip from "../ui-components/Tooltip";
import {MagnifyingGlassIcon} from "@heroicons/react/24/outline";
import _ from "lodash";
import Spinner from "../ui-components/Spinner";


const ServicePriceAnalysis = ({ service, selectedAp, setSelectedAp, setEdits, data }) => {
  const serviceId = service?.id;
  // context
  const { push } = useContext(NotificationsContext);
  const { country } = useContext(CountryContext);

  // hooks
  const intl = useIntl();
  const fetchApItems = useFetchApItems(serviceId);
  const dispatchAp = useDispatchServiceAp(serviceId);
  const countryApiRequest = useCountryAxiosInstance(country?.code);

  // state
  const [localAp, setLocalAp] = useState(_.cloneDeep(selectedAp));
  const [apParents, setApParents] = useState(null);
  const [apChildren, setApChildren] = useState(null);
  const [selectedApChild, setSelectedApChild] = useState(null);
  const [apItemsSearch, setApItemsSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDependencies, setShowDependencies] = useState(false);
  const [showCalculation, setShowCalculation] = useState(false);

  // utilities
  const DependenciesIcon = localAp.dependencies?.length > 0 ? PencilIcon : PlusIcon;
  const updateLocal = (field, value) => {
    setLocalAp((prev) => ({...prev, [field]: value}));
  }
  const buildApParentOption = (item) => {
    if (!item)
      return null;
    const {id, code, des_short} = item;
    return {
      label: `${code} - ${des_short}`,
      value: id,
      code,
      des_short,
      obj: item,
    }
  }
  const buildApChildrenOption = ({name, items}) => ({
    label: name,
    options: items.map(({code, des_short, value}) => ({
      label: `${code} - ${des_short}`,
      code,
      des_short,
      secondary: name,
      value,
    }))
  })
  const apChildrenOptions = apChildren
    ?.map((c) => ({...c, items: c.items
      ?.filter((i) => !localAp.items.find(({info}) => info?.value === i.value))}
      ?? []
    ))
    ?.filter(({items}) => !!items?.length > 0)
    ?.map(buildApChildrenOption)
    ?? [];
  const apItemCustomOptionFn = ({data, innerProps, innerRef}) => (
    <article
      ref={innerRef} {...innerProps}
      className="text-sm py-2 px-3 cursor-pointer hover:bg-blue-50"
    >
      <span className="font-semibold">{data.code}</span> - {data.des_short}
    </article>
  );
  const getCalculationIcon = (isSet, onClick, widthCls="w-8") => {
    const Icon = isSet ? PencilIcon : PlusIcon;
    return <Icon
      className={`${widthCls} text-gray-400 cursor-pointer flex-shrink-0`}
      onClick={onClick}
    />
  }
  const addSelectedChildToItems = () => {
    const [relPosix, parentName] = selectedApChild.value.split('|');
    setLocalAp((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: null,
          val_parent: parentName || null,
          val_rel_posix: relPosix,
          calculation: [],
          info: selectedApChild,
        }
      ]
    }));
    setSelectedApChild(null);
  }
  
  const isSaveAllowed = !!localAp.id_price_analysis_item &&
    !!localAp.calculation?.length &&
    !!localAp.items?.every((i) => !!i.calculation?.length);
  const renderDetailItem = ({info, val_parent, calculation}, idx) => (
    <div key={idx} className="border py-2 px-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div>
          <div className="mb-0.5 font-medium">
            <span className="font-semibold">{info.code}</span> - {info.des_short}
          </div>
          {!!val_parent && (
            <div className="text-xxs text-gray-500">
              {val_parent}
            </div>
          )}
        </div>
        {!calculation?.length && (
          <div className="mt-1.5">
            <Tooltip
              iconType="danger"
              text={intl.formatMessage({id: 'required_field'})}
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {getCalculationIcon(
          calculation?.length > 0,
          () => { setShowCalculation(idx); },
          "w-5"
        )}
        <TrashIcon
          className="text-red-600 w-5 cursor-pointer flex-shrink-0"
          onClick={() => { removeItemAt(idx); }}
        />
      </div>
    </div>
  );
  const renderDetailItemsList = () => {
    const items = localAp.items
      ?.filter(({info}) => {
        const mandatoryKeys = ['code', 'des_short'];
        const searchableKeys = [...mandatoryKeys, 'secondary'];
        return mandatoryKeys.every((k) => k in (info ?? {}))
          && (
            !apItemsSearch
            || searchableKeys.some((t) => info[t]
              .toLowerCase().includes(apItemsSearch.toLowerCase())
            )
          );
      })
    if (!items?.length)
      return <div className="relative w-full py-10">
        <Spinner styleType="white" />
      </div>;
    return items.map(renderDetailItem);
  }
  const removeItemAt = (idx) => {
    const removeAt = (arr, idx) => {
      arr.splice(idx, 1)
      return arr;
    }
    setLocalAp((prev) => ({...prev, items: removeAt(prev.items, idx)}));
  }

  // callbacks
  const saveAndExit = async () => {
    if (!isSaveAllowed)
      return;

    setSubmitting(true);
    try {
      const item = await dispatchAp(selectedAp, localAp);
      if (item) {
        push({type: 'success', title: intl.formatMessage({id: 'ap_edited_successfully'})});
        setEdits((prev) => prev + 1);
        setSelectedAp(null);
      }
    }
    catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
      throw error;
    }
    finally {
      setSubmitting(false);
    }
  }
  const deleteAp = async () => {
    setDeleting(true);
    try {
      await countryApiRequest.delete(`/services_price_analysis/${selectedAp.id}`);
      push({type: 'info', title: intl.formatMessage({id: 'ap_deleted_successfully'})});
      setEdits((prev) => prev + 1);
      setSelectedAp(null);
    }
    catch (error)  {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
      throw error;
    }
    finally {
      setDeleting(false);
    }
  };

  // refs
  const lastPriceAnalysis = useRef(null);

  useEffect(() => {
    fetchApItems()
      .then((result) => {
        setApParents(result);
        const selected = result.find((i) => i.id === localAp.id_price_analysis_item);
        if (!!selected)
          fetchApItems(selected).then(setApChildren);
        lastPriceAnalysis.current = selected?.price_analysis_item;
      });
  }, []);
  useEffect(() => {
    if (!lastPriceAnalysis.current || !localAp.price_analysis_item)
      return;

    updateLocal('items', []);
    fetchApItems(localAp.price_analysis_item).then(setApChildren);
  }, [localAp.id_price_analysis_item]);
  useEffect(() => {
    if (!apChildren?.length)
      return;
    
    setLocalAp((prev) => ({
      ...prev,
      items: prev.items
        ?.map((i) => {
          const target = apChildren
            .reduce((acc, {items}) => [...acc, ...items], [])
            .find(({value}) => value === `${i.val_rel_posix}|${i.val_parent}`);
          return {...i, info: target};
        })
        ?? []
    }));
  }, [JSON.stringify(apChildren)]);
  
  const mustShowCalculation = showCalculation === 'main' || (showCalculation !== false && showCalculation >= 0);
  if (showDependencies || mustShowCalculation) {
    const expressionEditorType = showDependencies ? "dependencies" : "calculation";
    // define expression to be edited
    let expression, target, setTarget;
    if (showDependencies) {
      target = localAp;
      setTarget = setLocalAp;
      expression = localAp.dependencies;
    } else {
      if (showCalculation === 'main') {
        target = localAp;
        setTarget = setLocalAp;
      } else {
        target = localAp.items?.[showCalculation];
        setTarget = (arg) => {
          setLocalAp((prev) => ({
            ...prev,
            items: prev.items
              ?.map((i, idx) => ({
                ...i,
                ...(idx === showCalculation
                  ? (typeof arg === 'function' ? arg(i.calculation) : arg)
                  : {}
                )
              }))
          }))
        }
      }
      if (!!target)
        expression = target.calculation;
    }
    
    // if expression has been defined, open its editor; if not, show a warning
    if (expression !== undefined) {
      return <ServiceConfiguratorBlockExpressionConfigurator
        setShowDependencies={showDependencies ? setShowDependencies : setShowCalculation}
        fullExpression={expression ?? []}
        newData={target}
        setNewData={setTarget}
        questions={data.filter((d) => d.type === "question" && !!d.input_type)}
        recursiveQuestions={data.filter(isValidRecursiveQuestion)}
        outputs={data.filter((d) =>
          d.type === "output"
          && ["calculation", "json_output_value"].includes(d.output_type)
        )}
        data={data}
        type={expressionEditorType}
        showKccOptimizationOutput={false}
        lockMissingSelectedOptions
        {...service?{ service: service}: {}}
        block={localAp}
      />
    } else {
      push({type: 'warning', title: intl.formatMessage({id: 'expression_editor_cannot_be_open'})});
      console.warn(`${expressionEditorType}: ${showDependencies || showCalculation}`)
    }
  }

  if (showCalculation) {
    return <ServiceConfiguratorBlockExpressionConfigurator
      setShowDependencies={setShowDependencies}
      fullExpression={localAp.dependencies ?? []}
      newData={localAp}
      setNewData={setLocalAp}
      questions={data.filter((d) => d.type === "question" && !!d.input_type)}
      recursiveQuestions={data.filter(isValidRecursiveQuestion)}
      outputs={data.filter((d) =>
        d.type === "output"
        && ["calculation", "json_output_value"].includes(d.output_type)
      )}
      data={data}
      type="dependencies"
      lockMissingSelectedOptions
      {... service?{ service: service}: {}}
      block={localAp}
    />
  }

  return <div className="flex flex-col gap-5 justify-between min-h-full">
    <div>
      <PageHeading
        contentActions={<CloseIcon onClick={() => { setSelectedAp(null); }} />}
      />
      <div className="flex flex-col gap-2">

        <SearchSelect
          label={intl.formatMessage({id: 'ap'})}
          value={buildApParentOption(localAp.price_analysis_item)}
          options={apParents?.map(buildApParentOption) ?? []}
          onChange={({value}) => {
            const selected = apParents.find((p) => p.id === value)
            lastPriceAnalysis.current = selected;

            setLocalAp((prev) => ({
              ...prev,
              id_price_analysis_item: value,
              price_analysis_item: selected,
            }))
          }}
          disabled={!apParents}
          CustomOption={apItemCustomOptionFn}
        />

        <Input
          label={intl.formatMessage({id: 'title'})}
          value={localAp.des_title}
          onChange={(e) =>  { updateLocal('des_title', e.target.value); }}
          errorMessage={intl.formatMessage({id: 'required_field'})}
          required
          validateOnMount={true}
        />

        <Textarea
          label={intl.formatMessage({id: 'description'})}
          rows={3}
          value={localAp.des_description}
          onChange={(e) => { updateLocal('des_description', e.target.value); }}
          errorMessage={intl.formatMessage({id: 'required_field'})}
          hideBottomMargin={true}
          textCls="text-sm"
          required
        />

        <div className="flex justify-between items-center my-4">
          <Label><FormattedMessage id="dependencies" /></Label>
          <DependenciesIcon
            className="text-gray-400 w-8 cursor-pointer flex-shrink-0"
            onClick={() => { setShowDependencies(true); }}
          />
        </div>

        <div className="flex justify-between items-center mb-5">
          <div className="flex gap-2 items-center">
            <Label><FormattedMessage id="ap_main_multiplier" /></Label>
            {!localAp.calculation?.length && <div className="mt-1.5">
              <Tooltip
                iconType="danger"
                text={intl.formatMessage({id: 'required_field'})}
              />
            </div>}
          </div>
          {getCalculationIcon(
            localAp.calculation?.length > 0,
            () => { setShowCalculation('main'); }
          )}
        </div>

        <div className={`mb-4 ${!localAp.id_price_analysis_item ? 'opacity-50 pointer-events-none' : ''}`}>
          <Label><FormattedMessage id="ap_detail_multipliers" /></Label>
          <div className="pl-2 pb-2 border-l-4 border-gray-100">
            <div className="flex gap-3 items-end mt-2">
              <div className="flex-1">
                <SearchSelect
                  value={selectedApChild}
                  options={apChildrenOptions}
                  onChange={setSelectedApChild}
                  disabled={!apChildren}
                />
              </div>
              <div className="mb-3">
                <Button
                  paddingCls="p-2.5"
                  disabled={!selectedApChild}
                  onClick={() => { addSelectedChildToItems(); }}
                >
                  <PlusIcon className="w-5" />
                </Button>
              </div>
            </div>
            {localAp.items?.length > 0
              ? <>
                <div className="flex items-center gap-2">
                  <MagnifyingGlassIcon className="w-5" />
                  <div className="flex-1">
                    <Input
                      type="text"
                      value={apItemsSearch}
                      hideBottomMargin={true}
                      onChange={(e) => { setApItemsSearch(e.target.value); }}
                      placeholder={intl.formatMessage({id: "search_multiplier"})}
                    />
                  </div>
                </div>
                <div className="mt-2 flex flex-col gap-2 overflow-y-auto" style={{maxHeight: "25em"}}>
                  {renderDetailItemsList()}
                </div>
              </>
              : <div className="flex items-center justify-center w-full h-24 text-gray-500 bg-gray-100 rounded-xl">
                <FormattedMessage id="no_detail_multipliers" />
              </div>
            }
          </div>
        </div>

        <Toggle
          label={intl.formatMessage({id: 'active'})}
          checked={localAp.flg_active}
          onChange={(status) => { updateLocal('flg_active', status); }}
          marginCls=""
        />

      </div>
    </div>
    <div className="flex items-center justify-between mt-2">
      <Button
        styleType="danger"
        onClick={async () => { await deleteAp(); }}
        submitting={deleting}
      >
        <TrashIcon className="w-4" />
        <FormattedMessage id="delete" />
      </Button>
      <Button
        onClick={async () => { await saveAndExit(); }}
        submitting={submitting}
        disabled={!isSaveAllowed}
      >
        <CheckIcon className="w-4" />
        <FormattedMessage id="save" />
      </Button>
    </div>
  </div>
}

export default ServicePriceAnalysis;